import { HelpCircle } from 'lucide-react';
import { useTour } from '../../contexts/TourContext';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

interface SectionTourTriggerProps {
  pageKey: string;
  sectionKey: string;
  className?: string;
  variant?: 'button' | 'icon' | 'link';
  size?: 'sm' | 'default';
  label?: string;
}

/**
 * A button/icon that triggers a section-level tour.
 * Place this in the header of each section/tab to allow users to learn about that section.
 */
export function SectionTourTrigger({
  pageKey,
  sectionKey,
  className,
  variant = 'icon',
  size = 'sm',
  label = 'Tour this section',
}: SectionTourTriggerProps) {
  const { startSectionTour, isSectionTourActive, isTourActive } = useTour();

  const handleClick = () => {
    startSectionTour(pageKey, sectionKey);
  };

  // Don't show if another tour is active
  if (isSectionTourActive || isTourActive) {
    return null;
  }

  if (variant === 'icon') {
    return (
      <button
        onClick={handleClick}
        className={cn(
          'p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-primary/20',
          className
        )}
        title={label}
        aria-label={label}
      >
        <HelpCircle className={size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} />
      </button>
    );
  }

  if (variant === 'link') {
    return (
      <button
        onClick={handleClick}
        className={cn(
          'text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline',
          'flex items-center gap-1',
          className
        )}
      >
        <HelpCircle className="h-3.5 w-3.5" />
        {label}
      </button>
    );
  }

  // Button variant
  return (
    <Button
      variant="ghost"
      size={size}
      onClick={handleClick}
      className={cn('gap-1.5', className)}
    >
      <HelpCircle className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
      {label}
    </Button>
  );
}
